import { theme } from '@Guidelines/Theme/theme';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import ClearFieldValueDialog from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/ConfirmationDialogs/ClearFieldValueDialog';
import LeaveFieldEmptyDialog from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/ConfirmationDialogs/LeaveFieldEmptyDialog';
import { css } from '@emotion/react';
import * as React from 'react';
import { Button } from '@verclam/ui-kit';

/*
 * Sticky offsets are measured from the scrollport minus the Sidebar content padding (--ui-kit-space-md,
 * 1rem). Negative bottom / side margins and a matching bottom offset make the bar span the full width and
 * sit on the very bottom edge of the scrolling area, so no content can pass below it.
 */
const actionsStyle = css`
	position: sticky;
	bottom: -1rem;
	z-index: 2;
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 0.5rem;
	margin: 1rem -1rem -1rem;
	padding: 0.75rem 1rem;
	background-color: ${theme.colors.surface};
	border-top: 1px solid ${theme.colors.borderStrong};
`;

const clearStyle = css`
	margin-left: auto;
`;

type Props = {
	/** The draft differs from the saved value. */
	readonly isDirty: boolean;
	readonly isSubmitting: boolean;
	readonly isConfirming: boolean;
	/** The saved value is refused by the template and replaced by nothing. */
	readonly canLeaveEmpty: boolean;
	readonly canConfirm: boolean;
	/** The saved value is empty: offers to move the focus to the control. */
	readonly canComplete: boolean;
	/** The saved value is not empty: offers to empty the draft. */
	readonly canClear: boolean;
	readonly onCancel: () => void;
	readonly onConfirm: () => void;
	readonly onComplete: () => void;
	readonly onClear: () => void;
	readonly onLeaveEmpty: () => void;
};

/** The panel's actions. A modified draft offers Enregistrer / Annuler; an untouched one offers the gestures that need no draft. */
const FieldPanelActions: React.FC<Props> = ({
	isDirty,
	isSubmitting,
	isConfirming,
	canLeaveEmpty,
	canConfirm,
	canComplete,
	canClear,
	onCancel,
	onConfirm,
	onComplete,
	onClear,
	onLeaveEmpty,
}) => {
	const [isClearAsked, setIsClearAsked] = React.useState(false);
	const [isLeaveEmptyAsked, setIsLeaveEmptyAsked] = React.useState(false);

	const handleAskClear = React.useCallback(() => {
		setIsClearAsked(true);
	}, []);

	const handleHideClear = React.useCallback(() => {
		setIsClearAsked(false);
	}, []);

	const handleAskLeaveEmpty = React.useCallback(() => {
		setIsLeaveEmptyAsked(true);
	}, []);

	const handleHideLeaveEmpty = React.useCallback(() => {
		setIsLeaveEmptyAsked(false);
	}, []);

	return (
		<div css={actionsStyle}>
			<ConditionalLoader data={isDirty}>
				<Button type="submit" label="Enregistrer" loading={isSubmitting} />
				<Button
					type="button"
					severity="secondary"
					outlined
					label="Annuler"
					disabled={isSubmitting}
					onClick={onCancel}
				/>
			</ConditionalLoader>
			<ConditionalLoader data={!isDirty && canConfirm}>
				<Button
					type="button"
					severity="success"
					label="Marquer vérifiée"
					loading={isConfirming}
					onClick={onConfirm}
				/>
			</ConditionalLoader>
			<ConditionalLoader data={!isDirty && canComplete}>
				<Button type="button" severity="secondary" label="Compléter" onClick={onComplete} />
			</ConditionalLoader>
			<ConditionalLoader data={!isDirty && canLeaveEmpty}>
				<Button
					type="button"
					severity="secondary"
					outlined
					label="Laisser vide"
					disabled={isSubmitting}
					onClick={handleAskLeaveEmpty}
				/>
			</ConditionalLoader>
			<ConditionalLoader data={canClear}>
				<span css={clearStyle}>
					<Button
						type="button"
						text
						severity="danger"
						label="Vider"
						disabled={isSubmitting}
						onClick={handleAskClear}
					/>
				</span>
			</ConditionalLoader>

			<ClearFieldValueDialog visible={isClearAsked} onHide={handleHideClear} onAccept={onClear} />
			<LeaveFieldEmptyDialog
				visible={isLeaveEmptyAsked}
				onHide={handleHideLeaveEmpty}
				onAccept={onLeaveEmpty}
			/>
		</div>
	);
};

export default FieldPanelActions;
